/* WF-14 · multi-location ops — the shell the eight ML surfaces share.
   /etablissements itself is the Orchestrator’s onboarding screen (ON-18, on
   main); the ML frames mount beneath it. The wireframe assumes navigation into
   these surfaces and does not draw it, so this adds only the switcher.
   Noted in Builder_B_Status.

   Batch 22 (Roadmap 0.4) — aligned to the six-word menu (D-70 · Scope v5 §4):
   these tools live under Paramètres › Établissements, which the sidebar shows
   from the second establishment on. They lead back to that list; and in a
   one-establishment workspace, where the menu says « Mon établissement » and
   there is nothing to configure in bulk, they say so and send there instead
   of drawing a network of one (D-63). */
import { Link, NavLink, Outlet, useNavigate, useOutletContext } from 'react-router-dom'
import { Button, EmptyState, Skeleton } from '@/ui'
import { useLocations } from '@/lib/api'
import { useMultiLocation, type MultiLocation } from './api'

export const ML_SURFACES = [
  { frame: 'ML-01', to: 'configurer', label: 'Configurer plusieurs établissements' },
  { frame: 'ML-02', to: 'import', label: 'Import CSV' },
  { frame: 'ML-03', to: 'groupes', label: 'Groupes' },
  { frame: 'ML-04', to: 'preparation', label: 'Grille de préparation' },
  { frame: 'ML-05', to: 'vagues', label: 'Activation par vagues' },
  { frame: 'ML-06', to: 'comparer', label: 'Comparer les établissements' },
  { frame: 'ML-07', to: 'appliquer', label: 'Appliquer à plusieurs' },
  { frame: 'ML-08', to: 'appliquer/resultat', label: 'Résultat de l’application' },
] as const

export default function EtabShell() {
  const { data, isPending, isError, refetch } = useMultiLocation()
  const { data: places, isPending: placesPending } = useLocations()
  const nav = useNavigate()
  if (!placesPending && (places?.length ?? 0) < 2) return (
    <div style={{ display: 'grid', gap: 18, maxWidth: 760 }}>
      <EmptyState title="Un seul établissement."
        why="Ces outils règlent plusieurs établissements à la fois. Avec un seul, tout se règle dans Mon établissement."
        action={<>
          <Button variant="primary" size="sm" onClick={() => nav('/etablissement')}>Mon établissement</Button>
          <Button variant="quiet" size="sm" onClick={() => nav('/etablissements?ajouter=1')}>Ajouter un établissement</Button>
        </>} />
    </div>
  )
  return (
    <div style={{ display: 'grid', gap: 18, maxWidth: 980 }}>
      <p className="f-help" style={{ margin: 0 }}><Link to="/etablissements">‹ Établissements</Link></p>
      <nav className="tabs" aria-label="Opérations multi-établissements">
        {ML_SURFACES.map(s => (
          <NavLink key={s.frame} to={`/etablissements/${s.to}`} end
            data-frame-link={s.frame}
            className={({ isActive }) => 'tab' + (isActive ? ' on' : '')}>
            {s.label}
          </NavLink>
        ))}
      </nav>
      {isPending && (
        <div style={{ display: 'grid', gap: 10 }}>
          <Skeleton width="42%" /><Skeleton /><Skeleton width="65%" />
        </div>
      )}
      {isError && (
        <EmptyState title="Impossible de charger les établissements."
          why="Votre connexion a peut-être sauté — rien n’a été perdu."
          action={<Button variant="primary" size="sm" onClick={() => refetch()}>Réessayer</Button>} />
      )}
      {data && <Outlet context={data} />}
    </div>
  )
}

export const useML = () => useOutletContext<MultiLocation>()
